import { useState } from 'react';

interface SuiteAnterieure {
  id: string;
  statut: string;
  created_at: string;
  sessions: any[];
  plaintes: any[];
  comptesRendus: any[];
}

interface SuitesAnterieuresSectionProps {
  suites: SuiteAnterieure[];
  onDuplicate: (suite: SuiteAnterieure) => void;
}

function formatDate(iso: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function SuiteCard({ suite, onDuplicate }: { suite: SuiteAnterieure; onDuplicate: (suite: SuiteAnterieure) => void }) {
  const [ouvert, setOuvert] = useState(false);

  const nbSoins = suite.sessions.length;
  const nbPlaintes = suite.plaintes.length;
  const nbCR = suite.comptesRendus.length;
  const terminee = suite.statut !== 'en_cours';

  return (
    <div className="border border-amber-200 rounded-xl overflow-hidden bg-amber-50/40">
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={() => setOuvert(!ouvert)}
          className="flex-1 flex items-center justify-between gap-3 px-4 py-3 hover:bg-amber-50 transition-colors cursor-pointer text-left min-w-0"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                Suite de soins du {formatDate(suite.created_at)}
              </p>
              <p className="text-xs text-gray-500">
                {nbSoins} soin{nbSoins > 1 ? 's' : ''} · {nbPlaintes} plainte{nbPlaintes > 1 ? 's' : ''}
                {nbCR > 0 ? ` · ${nbCR} compte${nbCR > 1 ? 's' : ''} rendu${nbCR > 1 ? 's' : ''}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap ${
                terminee ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'
              }`}
            >
              {terminee ? 'Terminée' : 'En cours'}
            </span>
            <i className={`text-gray-400 ${ouvert ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
          </div>
        </button>
        <button
          onClick={() => onDuplicate(suite)}
          className="mr-3 px-3 py-1.5 rounded-lg text-xs font-semibold text-amber-700 bg-white border border-amber-200 hover:bg-amber-100 cursor-pointer whitespace-nowrap flex items-center gap-1.5 flex-shrink-0 transition-colors"
        >
          <i className="ri-file-copy-line w-3.5 h-3.5 flex items-center justify-center"></i>
          Dupliquer
        </button>
      </div>

      {ouvert && (
        <div className="px-4 pb-4 space-y-4 border-t border-amber-100 pt-3">
          {/* Soins */}
          {nbSoins > 0 && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5">
                                Soins administrés
              </p>
              <div className="space-y-1.5">
                {suite.sessions.map((s) => (
                  <div key={s.id} className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded whitespace-nowrap flex-shrink-0 mt-0.5">
                      {s.date_soin ? new Date(s.date_soin).toLocaleDateString('fr-FR') : '—'}
                    </span>
                    <span className="text-sm text-gray-800">{s.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Plaintes */}
          {nbPlaintes > 0 && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5">
                                Plaintes & symptômes
              </p>
              <div className="space-y-1.5">
                {suite.plaintes.map((p) => (
                  <div key={p.id} className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded whitespace-nowrap flex-shrink-0 mt-0.5">
                      {p.date_plainte ? new Date(p.date_plainte).toLocaleDateString('fr-FR') : '—'}
                    </span>
                    <span className="text-sm text-gray-800">{p.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comptes rendus */}
          {nbCR > 0 && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5">
                                Comptes rendus de relève
              </p>
              <div className="space-y-1.5">
                {suite.comptesRendus.map((c) => (
                  <div key={c.id} className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded whitespace-nowrap flex-shrink-0 mt-0.5">
                      {formatDate(c.created_at)}
                    </span>
                    <span className="text-sm text-gray-800">
                      {c.contenu || c.etat_patient || c.mesures_prises || c.instructions_suivant || 'Compte rendu'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {nbSoins === 0 && nbPlaintes === 0 && nbCR === 0 && (
            <p className="text-sm text-gray-400 italic">Aucun élément enregistré dans cette suite.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function SuitesAnterieuresSection({ suites, onDuplicate }: SuitesAnterieuresSectionProps) {
  if (suites.length === 0) return null;

  return (
    <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-100">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    Suites de soins précédentes
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Soins et plaintes enregistrés lors des suites de soins antérieures pour cette consultation.
        </p>
      </div>
      <div className="p-6 space-y-3">
        {suites.map((s) => (
          <SuiteCard key={s.id} suite={s} onDuplicate={onDuplicate} />
        ))}
      </div>
    </section>
  );
}